import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Flame, Snowflake, Calendar, Flame as FlameIcon, Activity, Trophy, Plus, ArrowLeft, Zap } from 'lucide-react';
import {
  getMatches, computeStreak, getCachedName,
  computeWinStreak, estimateCalories, getAllElo,
} from '../lib/localStore';
import BottomNav from '../components/BottomNav';

const DAY_INITIAL = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function CalendarRing({ streak }) {
  if (!streak.dow && streak.dow !== 0) return null;
  // Show 6 weeks back, mark dominant DOW circles
  const today = new Date();
  const cells = [];
  for (let w = 5; w >= 0; w--) {
    const d = new Date(today);
    d.setDate(d.getDate() - (today.getDay() - streak.dow) - w * 7);
    cells.push(d);
  }
  return (
    <div className="flex justify-center gap-2 sm:gap-3">
      {cells.map((d, i) => {
        const isPast = d <= today;
        const filled = isPast && i >= cells.length - streak.weeksInARow;
        const isMostRecent = i === cells.length - 1 && streak.frozen;
        return (
          <div key={i} className="flex flex-col items-center gap-1">
            <div className={`relative w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 transition-all ${
              filled
                ? 'border-orange-500 bg-orange-500/20 text-orange-500'
                : isMostRecent
                  ? 'border-cyan-400 bg-cyan-400/10 text-cyan-400'
                  : 'border-muted bg-muted/30 text-muted-foreground'
            }`}>
              {filled ? <Flame className="h-4 w-4 fill-orange-500" /> : isMostRecent ? <Snowflake className="h-4 w-4" /> : <span className="text-[10px]">{DAY_INITIAL[d.getDay()]}</span>}
            </div>
            <span className="text-[9px] text-muted-foreground">{d.getDate()}/{d.getMonth() + 1}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function MyStreaks() {
  const navigate = useNavigate();
  const matches = useMemo(() => getMatches(), []);
  const streak = useMemo(() => computeStreak(), []);
  const userName = getCachedName();
  const winStreak = computeWinStreak(userName);
  const eloMap = getAllElo();
  const myElo = userName ? (eloMap[userName.toLowerCase()] || 1200) : null;

  // Aggregate stats
  const stats = useMemo(() => {
    const me = (userName || '').toLowerCase();
    let played = 0, won = 0, totalSec = 0;
    matches.forEach(m => {
      const isA = m.sideA?.toLowerCase() === me;
      const isB = m.sideB?.toLowerCase() === me;
      if (!isA && !isB) return;
      played += 1;
      if ((isA && m.winner === 'A') || (isB && m.winner === 'B')) won += 1;
      totalSec += m.durationSec || 0;
    });
    return { played, won, lost: played - won, totalSec, calories: estimateCalories(totalSec) };
  }, [matches, userName]);

  const recent = matches.slice(0, 10);

  return (
    <div className="min-h-screen bg-background safe-top safe-bottom pb-24" data-testid="my-streaks-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="h-9 w-9" data-testid="streaks-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="font-heading text-xl font-extrabold uppercase tracking-tight flex-1">My Cardio</h1>
      </div>

      <div className="px-4 py-5 space-y-5 max-w-2xl mx-auto">
        {/* Hero streak card */}
        <Card className={`overflow-hidden border-2 ${streak.weeksInARow > 0 && !streak.frozen ? 'border-orange-500/50' : streak.frozen ? 'border-cyan-400/50' : 'border-border'}`}>
          <CardContent className="p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Cardio Streak</p>
                {streak.weeksInARow > 0 ? (
                  <h2 className="font-heading text-2xl font-extrabold flex items-center gap-2" data-testid="streak-headline">
                    {streak.frozen ? (
                      <>
                        <Snowflake className="h-7 w-7 text-cyan-400" />
                        <span>Streak Frozen</span>
                      </>
                    ) : (
                      <>
                        <Flame className="h-7 w-7 text-orange-500 fill-orange-500" />
                        <span>{streak.weeksInARow}-Week {streak.dayName} Streak</span>
                      </>
                    )}
                  </h2>
                ) : (
                  <h2 className="font-heading text-xl font-bold text-muted-foreground" data-testid="streak-headline">
                    No streak yet — play 2+ matches on the same weekday
                  </h2>
                )}
              </div>
            </div>
            <CalendarRing streak={streak} />
            {streak.frozen && (
              <div className="rounded-lg bg-cyan-400/10 border border-cyan-400/30 p-3 text-xs text-center">
                ❄️ You missed your {streak.dayName} session. Play this week to thaw the streak!
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-3 text-center">
              <Activity className="h-4 w-4 mx-auto mb-1 text-primary" />
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Matches</p>
              <p className="font-heading text-2xl font-extrabold" data-testid="stat-matches">{stats.played}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <Trophy className="h-4 w-4 mx-auto mb-1 text-secondary" />
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Won</p>
              <p className="font-heading text-2xl font-extrabold text-secondary" data-testid="stat-won">{stats.won}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <Zap className="h-4 w-4 mx-auto mb-1 text-orange-500" />
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Win Streak</p>
              <p className="font-heading text-2xl font-extrabold text-orange-500" data-testid="stat-win-streak">{winStreak} 🔥</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <FlameIcon className="h-4 w-4 mx-auto mb-1 text-red-500" />
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Calories</p>
              <p className="font-heading text-2xl font-extrabold" data-testid="stat-calories">{stats.calories}</p>
              <p className="text-[10px] text-muted-foreground">kcal estimated</p>
            </CardContent>
          </Card>
        </div>

        {/* ELO Badge */}
        {myElo != null && (
          <Card className="border-primary/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Your ELO</p>
                <p className="font-heading text-3xl font-extrabold text-primary" data-testid="my-elo">{myElo}</p>
              </div>
              <Badge className="bg-primary/15 text-primary border-primary/30">
                {myElo >= 1400 ? 'Pro' : myElo >= 1300 ? 'Advanced' : myElo >= 1200 ? 'Intermediate' : 'Beginner'}
              </Badge>
            </CardContent>
          </Card>
        )}

        {/* Recent Matches */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">Recent</h3>
            <Badge variant="outline" className="text-[10px]">{matches.length} total</Badge>
          </div>
          {recent.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground text-sm">
                <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                Play your first Quick Match to start tracking!
                <Button onClick={() => navigate('/play')} className="mt-4" data-testid="no-matches-cta">
                  <Plus className="h-4 w-4 mr-1" /> Start Match
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2" data-testid="recent-matches-list">
              {recent.map(m => {
                const me = (userName || '').toLowerCase();
                const isA = m.sideA?.toLowerCase() === me;
                const won = (isA && m.winner === 'A') || (!isA && m.winner === 'B');
                return (
                  <Card key={m.id} className="hover:bg-muted/30 transition-colors">
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className={`w-1 h-12 rounded-full ${won ? 'bg-primary' : 'bg-muted-foreground/30'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {m.sideA} <span className="text-muted-foreground">vs</span> {m.sideB}
                        </p>
                        <p className="text-[11px] text-muted-foreground font-mono">
                          {m.games.map(g => `${g.scoreA}-${g.scoreB}`).join(', ')} • {new Date(m.completedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge variant={won ? 'default' : 'outline'} className="text-[10px] uppercase">
                        {won ? 'Won' : 'Lost'}
                      </Badge>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
